import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithCustomToken, auth } from '../firebase';
import { schedulerApiFetch } from '../api/schedulerClient';
import PortalOAuthSkeleton from '../components/portal-oauth/PortalOAuthSkeleton';
import { usePortalUi } from '../contexts/PortalUiContext';
import { parseBooleanQueryParam, writePortalUiFlags } from '../portal-oauth/uiFlags';

const NEMT_COVER_IMAGE =
  'https://provider.nemtbusiness.com/assets/images/branding/nemt-cover-image.jpg';

/**
 * NEMT portal redirect only — see docs/NEMT_PORTAL_HANDOFF_AND_CONFIG_SYNC.md.
 * Does not alter normal SignInPage flow.
 */
function PortalOAuthPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setPortalUiFlags, resetPortalUiFlags } = usePortalUi();
  const [error, setError] = useState(null);

  const tokenQ = searchParams.get('token');
  const baseUrlQ =
    searchParams.get('base_url') ||
    searchParams.get('baseUrl') ||
    searchParams.get('api_base_url');
  const envQ = searchParams.get('env') || 'dev';
  const isShowSidenavQ = searchParams.get('isShowSidenav');
  const isDisablePlatformQ = searchParams.get('isDisablePlatform');

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!tokenQ?.trim()) {
        if (!cancelled) {
          setError('Missing required query parameter: token.');
        }
        return;
      }

      try {
        const isShowSidenav = parseBooleanQueryParam(isShowSidenavQ, true);
        const isDisablePlatform = parseBooleanQueryParam(isDisablePlatformQ, false);

        const extraHeaders = {};
        const secret = process.env.REACT_APP_PORTAL_OAUTH_SECRET;
        if (secret) {
          extraHeaders['X-Portal-OAuth-Secret'] = secret;
        }

        const body = {
          token: tokenQ.trim(),
          env: envQ.trim(),
        };
        if (baseUrlQ?.trim()) {
          body.base_url = baseUrlQ.trim();
        }

        const meEndpoint = body.base_url
          ? `${String(body.base_url).trim().replace(/\/$/, '')}/users/me`
          : '(server requires base_url)';
        // eslint-disable-next-line no-console
        console.log('[Portal OAuth] NEMT /users/me endpoint (server will call):', meEndpoint, {
          env: body.env,
          base_url: body.base_url || null,
          isShowSidenav,
          isDisablePlatform,
        });

        const res = await schedulerApiFetch('portal-oauth/session', {
          method: 'POST',
          body,
          headers: extraHeaders,
        });

        const text = await res.text();
        let data;
        try {
          data = text ? JSON.parse(text) : {};
        } catch {
          throw new Error('Invalid response from planner API');
        }

        if (!res.ok) {
          const detail = data.detail || data.message || text || res.statusText;
          const errMsg = typeof detail === 'string' ? detail : 'Portal sign-in failed';
          // eslint-disable-next-line no-console
          console.error('[Portal OAuth] /portal-oauth/session failed:', {
            status: res.status,
            meEndpoint,
            detail: errMsg,
            response: data,
          });
          throw new Error(errMsg);
        }

        if (!data.custom_token) {
          throw new Error('Portal sign-in did not return a session token');
        }

        // Persist chrome flags before auth state flips so AppContent never flashes sidenav.
        if (!cancelled) {
          setPortalUiFlags({ isShowSidenav, isDisablePlatform });
        } else {
          writePortalUiFlags({ isShowSidenav, isDisablePlatform });
        }

        await signInWithCustomToken(auth, data.custom_token);

        const nemtBase =
          (data.nemt_api_base_url && String(data.nemt_api_base_url).trim()) ||
          (baseUrlQ && String(baseUrlQ).trim().replace(/\/$/, '')) ||
          '';
        try {
          sessionStorage.setItem(
            'portal_oauth_handoff',
            JSON.stringify({
              api_jwt_token: tokenQ.trim(),
              server_environment: data.server_environment || envQ.trim() || 'dev',
              nemt_api_base_url: nemtBase,
            })
          );
        } catch { /* storage quota / private mode — non-critical */ }

        // Signal other tabs that a new portal login happened so they
        // clear stale cache and reload with the current user.
        try {
          localStorage.setItem(
            'portal_oauth_login',
            JSON.stringify({
              uid: data.uid,
              ts: Date.now(),
              isShowSidenav,
              isDisablePlatform,
            })
          );
        } catch { /* storage quota / private mode — non-critical */ }

        if (!cancelled) {
          navigate('/', { replace: true });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[Portal OAuth] sign-in error:', e?.message || e, e);
        if (!cancelled) {
          resetPortalUiFlags();
          setError(e.message || 'Portal sign-in failed');
        }
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [tokenQ, baseUrlQ, envQ, isShowSidenavQ, isDisablePlatformQ, navigate, setPortalUiFlags, resetPortalUiFlags]);

  if (error) {
    return (
      <div className="relative min-h-screen min-h-[100dvh] font-inter text-slate-900 antialiased">
        <div className="absolute inset-0">
          <img className="h-full w-full object-cover" src={NEMT_COVER_IMAGE} alt="" />
          <div className="bg-is-primary/50 absolute inset-0 mix-blend-multiply" aria-hidden />
          <div className="bg-is-primary/50 absolute inset-0 mix-blend-multiply" aria-hidden />
        </div>
        <div className="relative grid min-h-screen place-content-center px-4 py-32">
          <div className="flex w-[350px] max-w-full flex-col items-center bg-white p-8 sm:p-16 md:w-[520px] md:p-12">
            <h1 className="text-balance text-2xl font-semibold tracking-tight sm:text-3xl">Couldn’t connect</h1>
            <p className="mt-3 text-sm text-slate-600 sm:text-base">Try again from the NEMT app or go to sign in.</p>
            <p className="mt-6 break-words text-sm leading-relaxed text-slate-500">{error}</p>
            <button
              type="button"
              onClick={() => navigate('/', { replace: true })}
              className="btn-primary mt-8 w-full text-base sm:mt-10"
            >
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isDisablePlatform = parseBooleanQueryParam(isDisablePlatformQ, false);

  return <PortalOAuthSkeleton isDisablePlatform={isDisablePlatform} />;
}

export default PortalOAuthPage;
